import {
    createContext,
    FC,
    PropsWithChildren,
    useCallback,
    useContext,
    useEffect,
    useMemo,
} from "react";
import { AffiliateTracker } from "@finbackoffice/fe-core";
import { useSearchParams } from "next/navigation";
import { useRuntimeConfig } from "../hooks";
import { AffiseInitScript, affiseTriggerRegistration } from "../components";
import { BrowserStorageContext, BrowserStorageIdsEnum } from "./browser.storage.provider";

type IAffiliateContext = {
    getTracker: () => string | undefined;
    getAffiliateId: () => string | undefined;
    triggerRegistration: () => void;
};

export const AffiliateTrackerContext = createContext<IAffiliateContext>(null as any);

export const AffiliateTrackerProvider: FC<PropsWithChildren> = ({ children }) => {
    const searchParams = useSearchParams();
    const COMMON_SITE_CONFIGS = useRuntimeConfig("COMMON_SITE_CONFIGS");
    const { get } = useContext(BrowserStorageContext);

    useEffect(() => {
        if (
            COMMON_SITE_CONFIGS.affiliateTracker.enable &&
            COMMON_SITE_CONFIGS.affiliateTracker.name === AffiliateTracker.Smartico
        ) {
            if (searchParams?.has(BrowserStorageIdsEnum.BTAG)) {
                sessionStorage.setItem(
                    BrowserStorageIdsEnum.BTAG,
                    searchParams.get(BrowserStorageIdsEnum.BTAG) || "",
                );
            }

            if (searchParams?.has(BrowserStorageIdsEnum.AFF_ID)) {
                sessionStorage.setItem(
                    BrowserStorageIdsEnum.AFF_ID,
                    searchParams.get(BrowserStorageIdsEnum.AFF_ID) || "",
                );
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const getTracker = useCallback(() => {
        switch (COMMON_SITE_CONFIGS.affiliateTracker.name) {
            case AffiliateTracker.Affise:
                if (get(BrowserStorageIdsEnum.AFF_CLICK)) {
                    return `${get(BrowserStorageIdsEnum.UTM_CAMPAIGN) ? `${get(BrowserStorageIdsEnum.UTM_CAMPAIGN)}-` : ""}${get(BrowserStorageIdsEnum.AFF_CLICK)}`;
                }
                return undefined;
            case AffiliateTracker.Smartico:
                if (
                    typeof document !== "undefined" &&
                    sessionStorage.getItem(BrowserStorageIdsEnum.BTAG)
                ) {
                    return sessionStorage.getItem(BrowserStorageIdsEnum.BTAG) || "";
                }
                return undefined;
            default:
                return undefined;
        }
    }, [COMMON_SITE_CONFIGS.affiliateTracker.name, get]);

    const getAffiliateId = useCallback(() => {
        switch (COMMON_SITE_CONFIGS.affiliateTracker.name) {
            case AffiliateTracker.Smartico:
                if (
                    typeof document !== "undefined" &&
                    sessionStorage.getItem(BrowserStorageIdsEnum.AFF_ID)
                ) {
                    return sessionStorage.getItem(BrowserStorageIdsEnum.AFF_ID) || "";
                }
                return undefined;
            default:
                return undefined;
        }
    }, [COMMON_SITE_CONFIGS.affiliateTracker.name]);

    const triggerRegistration = useCallback(() => {
        if (COMMON_SITE_CONFIGS.affiliateTracker.name === AffiliateTracker.Affise) {
            affiseTriggerRegistration();
        }
    }, [COMMON_SITE_CONFIGS.affiliateTracker.name]);

    const value = useMemo(
        () => ({
            getTracker,
            getAffiliateId,
            triggerRegistration,
        }),
        [getAffiliateId, getTracker, triggerRegistration],
    );

    return (
        <AffiliateTrackerContext.Provider value={value}>
            {COMMON_SITE_CONFIGS.affiliateTracker.enable &&
                COMMON_SITE_CONFIGS.affiliateTracker.name === AffiliateTracker.Affise && (
                    <AffiseInitScript />
                )}
            {children}
        </AffiliateTrackerContext.Provider>
    );
};
